import React, { useState, useRef, useCallback } from 'react';
import { Container, Button, Alert, Card, Spinner, Row, Col } from 'react-bootstrap';
import Webcam from 'react-webcam';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { analyzeCard } from '../services/cardAnalysis';
import api from '../services/api';
import './Scan.css';

function Scan() {
  const [scanning, setScanning] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cardData, setCardData] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState('');
  
  const { } = useAuth();
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "environment" // Use back camera for scanning cards
  };

  const resizeImage = (base64Str, maxWidth = 1024, maxHeight = 1024) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = base64Str;
    });
  };

  const handleAnalyzeCard = async (imageSrc) => {
    setAnalyzing(true);
    setError('');
    
    try {
      // Resize image before sending to prevent size issues
      console.log('Resizing image...');
      const resizedImage = await resizeImage(imageSrc);
      console.log('Image resized, sending to backend...');
      
      // Use the card analysis service
      const analysisResult = await analyzeCard(resizedImage);
      
      // Ensure recent sales is properly formatted
      if (analysisResult.recentSales && typeof analysisResult.recentSales[0] === 'object') {
        analysisResult.recentSales = analysisResult.recentSales.map(sale => 
          typeof sale === 'string' ? sale : `${sale.source} - ${sale.price}`
        );
      }

      setCardData(analysisResult);
      setAnalyzing(false);
    } catch (err) {
      console.error('Card analysis error:', err);
      console.error('Error stack:', err.stack);
      console.error('Error type:', err.name);
      
      // More specific error messages
      if (err.message.includes('Failed to fetch')) {
        setError(`Cannot connect to backend server at ${process.env.REACT_APP_API_URL || 'http://localhost:3001'}`);
      } else if (err.message.includes('NetworkError')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to analyze card. Please try again.');
      }
      setAnalyzing(false);
    }
  };

  const capture = useCallback(() => {
    if (!webcamRef.current) {
      setError('Camera not ready. Please try again.');
      return;
    }
    
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      setError('Failed to capture image. Please ensure camera permissions are granted.');
      return;
    }
    
    setCapturedImage(imageSrc);
    setScanning(false);
    handleAnalyzeCard(imageSrc);
  }, []);

  const saveCard = async () => {
    if (!cardData || !capturedImage) return;

    setError('');
    try {
      // Prepare card data
      const category = cardData.type.toLowerCase().replace(/\s+/g, '');
      
      // Save card data using MongoDB API
      await api.saveCard({
        ...cardData,
        imageData: capturedImage, // API will handle image upload
        category
      });

      setSuccess('Card saved successfully!');
      setTimeout(() => {
        navigate('/profile/cards');
      }, 2000);
    } catch (err) {
      setError('Failed to save card: ' + err.message);
    }
  };

  const startScanning = () => {
    setScanning(true);
    setCapturedImage(null);
    setCardData(null);
    setError('');
    setSuccess('');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedImage(e.target.result);
        handleAnalyzeCard(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setError('Please select a valid image file');
    }
  };

  return (
    <Container className="mt-4">
      <Card className="bg-dark text-white shadow">
        <Card.Header className="bg-primary text-center py-4">
          <h2 className="mb-0">
            <i className="bi bi-camera-fill me-2"></i>
            Scan Trading Card
          </h2>
        </Card.Header>
        <Card.Body className="p-4">
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          {!scanning && !capturedImage && (
            <div className="text-center py-5">
              <div className="mb-4">
                <i className="bi bi-credit-card display-1 text-primary"></i>
              </div>
              <Button
                variant="primary"
                size="lg"
                onClick={startScanning}
                className="px-5 py-3 rounded-pill shadow-lg me-3"
              >
                <i className="bi bi-camera me-2"></i>
                Use Camera
              </Button>
              <Button
                variant="outline-primary"
                size="lg"
                onClick={() => fileInputRef.current?.click()}
                className="px-5 py-3 rounded-pill"
              >
                <i className="bi bi-upload me-2"></i>
                Upload Image
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <p className="mt-3 text-muted">
                Use your camera or upload an image of your trading card
              </p>
            </div>
          )}

          {scanning && (
            <Card className="bg-black border-0 overflow-hidden">
              <div className="position-relative">
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={videoConstraints}
                  className="w-100"
                  onUserMediaError={(error) => {
                    setCameraError('Camera access denied. Please allow camera permissions and reload the page.');
                    setError('Camera access denied. Please check your browser permissions.');
                  }}
                  onUserMedia={() => {
                    setCameraError('');
                  }}
                />
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                  <div className="border border-3 border-white rounded-3 shadow" 
                       style={{ width: '80%', maxWidth: '500px', height: '70%', maxHeight: '350px' }}>
                  </div>
                </div>
                <div className="position-absolute bottom-0 start-0 w-100 p-4 text-center">
                  <Button
                    variant="success"
                    size="lg"
                    onClick={capture}
                    className="px-5 py-3 rounded-pill shadow-lg"
                  >
                    <i className="bi bi-camera-fill me-2"></i>
                    Capture Card
                  </Button>
                  <div className="mt-2">
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => {
                        // Use a test image
                        const testImage = 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/2wBDAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCwAA8A/9k=';
                        setCapturedImage(testImage);
                        setScanning(false);
                        handleAnalyzeCard(testImage);
                      }}
                      className="text-dark"
                    >
                      <i className="bi bi-image me-1"></i>
                      Test with Sample Image
                    </Button>
                  </div>
                </div>
              </div>
              {cameraError && (
                <Alert variant="warning" className="m-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {cameraError}
                </Alert>
              )}
            </Card>
          )}

          {analyzing && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
              <h4 className="text-primary">Analyzing your card...</h4>
              <p className="text-muted">Using AI to identify and value your card</p>
            </div>
          )}

          {cardData && capturedImage && (
            <Card className="bg-secondary border-0 shadow-lg">
              <Card.Body className="p-4">
                <Row>
                  <Col md={6} className="mb-4 mb-md-0">
                    <Card className="bg-dark border-0">
                      <Card.Img
                        src={capturedImage}
                        alt="Captured card"
                        className="rounded shadow"
                      />
                    </Card>
                  </Col>
                  <Col md={6}>
                    <h4 className="text-info mb-4 border-bottom border-secondary pb-2">
                      <i className="bi bi-info-circle-fill me-2"></i>
                      Card Analysis
                    </h4>
                    <Card className="bg-dark mb-3">
                      <Card.Body>
                        <Row className="g-3">
                          <Col xs={6}>
                            <small className="text-info">Type</small>
                            <p className="mb-0 fw-bold text-white">{cardData.type}</p>
                          </Col>
                          <Col xs={6}>
                            <small className="text-info">Name</small>
                            <p className="mb-0 fw-bold text-warning">{cardData.name}</p>
                          </Col>
                          <Col xs={6}>
                            <small className="text-info">Year</small>
                            <p className="mb-0 fw-bold text-white">{cardData.year}</p>
                          </Col>
                          <Col xs={6}>
                            <small className="text-info">Brand</small>
                            <p className="mb-0 fw-bold text-white">{cardData.brand}</p>
                          </Col>
                          <Col xs={6}>
                            <small className="text-info">Card #</small>
                            <p className="mb-0 fw-bold text-white">{cardData.cardNumber}</p>
                          </Col>
                          <Col xs={6}>
                            <small className="text-info">Condition</small>
                            <p className={`mb-0 fw-bold ${
                              cardData.condition?.includes('Mint') ? 'text-success' : 
                              cardData.condition?.includes('Excellent') ? 'text-warning' : 
                              'text-danger'
                            }`}>{cardData.condition}</p>
                          </Col>
                        </Row>
                        {cardData.specialFeatures && cardData.specialFeatures.length > 0 && (
                          <div className="mt-3">
                            <small className="text-info">Special Features</small>
                            <div className="mt-1">
                              {cardData.specialFeatures.map((feature, index) => (
                                <span key={index} className="badge bg-primary me-2 mb-1">
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                    
                    <Card className="bg-dark mb-3">
                      <Card.Body>
                        <h5 className="text-success mb-3">
                          <i className="bi bi-currency-dollar me-2"></i>
                          Market Value
                        </h5>
                        <h3 className="text-white mb-3">
                          <span className="text-success">{cardData.estimatedValue}</span>
                        </h3>
                        {cardData.averagePrice && (
                          <p className="mb-2">
                            <small className="text-muted">Average Sale Price: </small>
                            <span className="text-white fw-bold">${cardData.averagePrice}</span>
                          </p>
                        )}
                        {cardData.priceTrend && (
                          <p className="mb-3">
                            <small className="text-muted">Price Trend: </small>
                            <span className={`badge ${
                              cardData.priceTrend === 'increasing' ? 'bg-success' :
                              cardData.priceTrend === 'decreasing' ? 'bg-danger' :
                              'bg-secondary'
                            }`}>
                              <i className={`bi bi-graph-${cardData.priceTrend === 'increasing' ? 'up' : 'down'} me-1`}></i>
                              {cardData.priceTrend}
                            </span>
                          </p>
                        )}
                        {cardData.marketInsights && (
                          <Row className="mt-3 g-2">
                            <Col xs={4}>
                              <small className="text-muted d-block">Demand</small>
                              <span className={`badge ${
                                cardData.marketInsights.demandLevel === 'Very High' ? 'bg-danger' :
                                cardData.marketInsights.demandLevel === 'High' ? 'bg-warning' :
                                'bg-secondary'
                              }`}>{cardData.marketInsights.demandLevel}</span>
                            </Col>
                            <Col xs={4}>
                              <small className="text-muted d-block">eBay Listings</small>
                              <span className="text-white">
                                {cardData.marketInsights.totalFound || cardData.marketInsights.activeListings}
                              </span>
                            </Col>
                            <Col xs={4}>
                              <small className="text-muted d-block">Grade</small>
                              <span className={`badge bg-${
                                cardData.marketInsights.investmentGrade === 'A' ? 'success' :
                                cardData.marketInsights.investmentGrade === 'B' ? 'warning' :
                                'secondary'
                              }`}>{cardData.marketInsights.investmentGrade}</span>
                            </Col>
                          </Row>
                        )}
                        {(cardData.recentSalesDisplay || cardData.recentSales) && (cardData.recentSalesDisplay || cardData.recentSales).length > 0 && (
                          <div className="mt-3">
                            <small className="text-info">Recent Sales:</small>
                            <ul className="list-unstyled mt-2">
                              {(cardData.recentSalesDisplay || cardData.recentSales).map((sale, index) => (
                                <li key={index} className="text-light mb-2">
                                  <i className="bi bi-tag-fill text-success me-2"></i>
                                  <small>{typeof sale === 'string' ? sale : `${sale.platform} - $${sale.price} (${sale.condition})`}</small>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                    
                    {cardData.psaData && (
                      <Card className="bg-dark mb-3 border-2 border-primary">
                        <Card.Body>
                          <h5 className="text-primary mb-3">
                            <i className="bi bi-shield-check-fill me-2"></i>
                            PSA Certification
                          </h5>
                          <Row className="g-3">
                            <Col xs={6}>
                              <small className="text-info">PSA Grade</small>
                              <h4 className="mb-0 text-white">{cardData.psaData.grade}</h4>
                              <small className="text-muted">{cardData.psaData.gradeDescription}</small>
                            </Col>
                            <Col xs={6}>
                              <small className="text-info">Cert Number</small>
                              <p className="mb-0 fw-bold text-white">{cardData.psaData.certNumber}</p>
                            </Col>
                            <Col xs={6}>
                              <small className="text-info">Population</small>
                              <p className="mb-0 fw-bold text-white">{cardData.psaData.totalPopulation}</p>
                              <small className="text-muted">Total graded</small>
                            </Col>
                            <Col xs={6}>
                              <small className="text-info">Pop Higher</small>
                              <p className="mb-0 fw-bold text-white">{cardData.psaData.populationHigher}</p>
                              <small className="text-muted">Graded higher</small>
                            </Col>
                          </Row>
                          {cardData.psaData.rarityScore && (
                            <div className="mt-3">
                              <small className="text-info">Rarity</small>
                              <div>
                                <span className={`badge bg-${
                                  cardData.psaData.rarityScore === 'Extremely Rare' ? 'danger' :
                                  cardData.psaData.rarityScore === 'Very Rare' ? 'warning' :
                                  cardData.psaData.rarityScore === 'Rare' ? 'primary' :
                                  'secondary'
                                }`}>
                                  <i className="bi bi-gem me-1"></i>
                                  {cardData.psaData.rarityScore}
                                </span>
                              </div>
                            </div>
                          )}
                          {cardData.psaData.psaEnhancedValue && (
                            <div className="mt-3 p-3 bg-success bg-opacity-10 rounded">
                              <small className="text-success">PSA Graded Value</small>
                              <h4 className="mb-0 text-success">{cardData.psaData.psaEnhancedValue}</h4>
                              <small className="text-muted">
                                {cardData.psaData.gradeValueMultiplier}x multiplier applied
                              </small>
                            </div>
                          )}
                          {cardData.psaData.autographGrade && (
                            <div className="mt-3">
                              <small className="text-info">Autograph Grade</small>
                              <p className="mb-0 fw-bold text-white">{cardData.psaData.autographGrade}</p>
                              {cardData.psaData.primarySigners && cardData.psaData.primarySigners.length > 0 && (
                                <small className="text-muted">
                                  Signed by: {cardData.psaData.primarySigners.join(', ')}
                                </small>
                              )}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    )}
                    
                    <Card className={`bg-dark mb-4 border-2 ${
                      cardData.authenticity?.includes('Authentic') ? 'border-success' : 
                      cardData.authenticity?.includes('Likely') ? 'border-warning' : 
                      'border-danger'
                    }`}>
                      <Card.Body>
                        <small className="text-info">Authenticity Check</small>
                        <p className={`mb-0 fw-bold ${
                          cardData.authenticity?.includes('PSA Certified') || cardData.authenticity?.includes('Verified') ? 'text-success' : 
                          cardData.authenticity?.includes('Highly Likely') || cardData.authenticity?.includes('Likely Authentic') ? 'text-white' : 
                          cardData.authenticity?.includes('Possibly') ? 'text-warning' : 
                          'text-danger'
                        }`}>
                          <i className={`bi ${
                            cardData.authenticity?.includes('PSA Certified') || cardData.authenticity?.includes('Verified') ? 'bi-shield-check-fill' : 
                            cardData.authenticity?.includes('Highly Likely') || cardData.authenticity?.includes('Likely Authentic') ? 'bi-shield-check' : 
                            cardData.authenticity?.includes('Possibly') ? 'bi-shield' : 
                            'bi-shield-exclamation'
                          } me-2`}></i>
                          {cardData.authenticity}
                        </p>
                        {cardData.confidence && (
                          <small className="text-muted d-block mt-2">
                            Analysis Confidence: 
                            <span className={`ms-2 ${
                              cardData.confidence >= 80 ? 'text-success' :
                              cardData.confidence >= 50 ? 'text-white' :
                              'text-danger'
                            }`}>
                              {cardData.confidence}
                            </span>
                          </small>
                        )}
                      </Card.Body>
                    </Card>
                    
                    {/* Data Sources */}
                    {cardData.dataSources && cardData.dataSources.length > 0 && (
                      <Card className="bg-dark mb-3">
                        <Card.Body className="py-2">
                          <small className="text-info">Data Sources:</small>
                          <div className="mt-1">
                            {cardData.dataSources.map((source, index) => (
                              <span key={index} className="badge bg-secondary me-2">
                                <i className="bi bi-database me-1"></i>
                                {source.name}
                              </span>
                            ))}
                            {cardData.confidence && (
                              <span className="badge bg-primary">
                                {cardData.confidence}% Confidence
                              </span>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    )}
                    
                    {/* Population Report */}
                    {cardData.populationReport && (
                      <Card className="bg-dark mb-3">
                        <Card.Body className="py-2">
                          <small className="text-info">PSA Population:</small>
                          <Row className="mt-2">
                            <Col xs={6}>
                              <small className="text-muted">Total Graded</small>
                              <p className="mb-0 text-white">{cardData.populationReport.total}</p>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted">In This Grade</small>
                              <p className="mb-0 text-white">{cardData.populationReport.inGrade}</p>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    )}
                    
                    {/* Matched Card Info */}
                    {cardData.matchedCard && (
                      <Card className="bg-info bg-opacity-10 border-info mb-3">
                        <Card.Body className="py-2">
                          <small className="text-info">
                            <i className="bi bi-check-circle me-1"></i>
                            Matched: {cardData.matchedCard.name} ({cardData.matchedCard.score}% match)
                          </small>
                        </Card.Body>
                      </Card>
                    )}
                    
                    <div className="d-grid gap-2">
                      <Button
                        variant="success"
                        size="lg"
                        onClick={saveCard}
                        className="shadow"
                      >
                        <i className="bi bi-save me-2"></i>
                        Save to Collection
                      </Button>
                      <Button
                        variant="outline-primary"
                        onClick={startScanning}
                      >
                        <i className="bi bi-arrow-repeat me-2"></i>
                        Scan Another Card
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Scan;